/**
 * Vehicle Categories Step (replaces old FleetSizeStep)
 *
 * Counter cards for each vehicle category with +/- buttons.
 * Captures how many vehicles per category the client has.
 */

import React from 'react';
import { Car, Truck, Container, HardHat, Bike, Minus, Plus } from 'lucide-react';

const VEHICLE_CATEGORIES = [
  {
    id: 'VL',
    label: 'Voiture (VL)',
    description: 'VP, citadines, berlines, SUV',
    icon: Car,
    color: 'bg-blue-500',
    lightBg: 'bg-blue-50',
    examples: 'Clio, 208, Golf, Model 3',
  },
  {
    id: 'VU',
    label: 'Camionnette (VU)',
    description: 'Fourgons, utilitaires, combispaces',
    icon: Truck,
    color: 'bg-green-500',
    lightBg: 'bg-green-50',
    examples: 'Kangoo, Partner, Transit, Trafic',
  },
  {
    id: 'PL',
    label: 'Camion (PL)',
    description: 'Poids lourds > 3.5T',
    icon: Container,
    color: 'bg-amber-500',
    lightBg: 'bg-amber-50',
    examples: 'Master, Sprinter, Daily, FH',
  },
  {
    id: 'ENGIN_CHANTIER',
    label: 'Engin de chantier',
    description: 'Pelleteuses, chariots, nacelles',
    icon: HardHat,
    color: 'bg-purple-500',
    lightBg: 'bg-purple-50',
    examples: 'CAT 320, Bobcat, Manitou, JCB',
  },
  {
    id: 'MOTO',
    label: 'Moto / Deux-roues',
    description: 'Motos, scooters de service',
    icon: Bike,
    color: 'bg-rose-500',
    lightBg: 'bg-rose-50',
    examples: 'Yamaha TMAX, BMW C400, Honda PCX',
  },
];

function CounterInput({ value, onChange, min = 0, max = 100 }) {
  const increment = () => onChange(Math.min(max, value + 1));
  const decrement = () => onChange(Math.max(min, value - 1));

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={decrement}
        disabled={value <= min}
        className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
      >
        <Minus className="w-4 h-4 text-gray-600" />
      </button>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Math.max(min, Math.min(max, parseInt(e.target.value) || 0)))}
        className="w-14 h-10 bg-white border border-gray-300 rounded-lg text-center text-gray-900 font-bold focus:ring-2 focus:ring-amber-500 focus:border-transparent"
      />
      <button
        type="button"
        onClick={increment}
        disabled={value >= max}
        className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
      >
        <Plus className="w-4 h-4 text-gray-600" />
      </button>
    </div>
  );
}

export function FleetSizeStep({ data, updateData, onNext, onSkip }) {
  const categories = data.vehicleCategories || { VL: 0, VU: 0, PL: 0, ENGIN_CHANTIER: 0, MOTO: 0 };

  const handleCategoryChange = (categoryId, value) => {
    updateData({
      vehicleCategories: {
        ...categories,
        [categoryId]: value,
      },
    });
  };

  const totalVehicles = Object.values(categories).reduce((sum, v) => sum + v, 0);
  const companyName = data.companyName || 'votre entreprise';

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">
          Parfait{data.companyName ? `, ${companyName}` : ''} !
        </h2>
        <p className="text-gray-500">
          Combien de véhicules avez-vous dans chaque catégorie ?
        </p>
      </div>

      {/* Category counter cards */}
      <div className="space-y-3">
        {VEHICLE_CATEGORIES.map((category) => {
          const Icon = category.icon;
          const value = categories[category.id] || 0;

          return (
            <div
              key={category.id}
              className={`
                flex items-center gap-4 p-4 bg-white border rounded-xl shadow-sm transition-all
                ${value > 0 ? 'border-amber-300' : 'border-gray-200'}
              `}
            >
              <div className={`w-12 h-12 ${value > 0 ? category.color : 'bg-gray-200'} rounded-xl flex items-center justify-center transition-colors`}>
                <Icon className="w-6 h-6 text-white" />
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-gray-900 font-medium">{category.label}</h3>
                <p className="text-gray-400 text-sm truncate">{category.examples}</p>
              </div>

              <CounterInput
                value={value}
                onChange={(v) => handleCategoryChange(category.id, v)}
              />
            </div>
          );
        })}
      </div>

      {/* Total summary pill */}
      <div className="flex items-center justify-center">
        <div className={`
          px-6 py-3 rounded-full font-medium text-lg transition-all
          ${totalVehicles > 0
            ? 'bg-amber-50 border border-amber-300 text-amber-700'
            : 'bg-gray-50 border border-gray-200 text-gray-400'
          }
        `}>
          {totalVehicles > 0
            ? `${totalVehicles} véhicule${totalVehicles > 1 ? 's' : ''} au total`
            : 'Aucun véhicule pour le moment'
          }
        </div>
      </div>

      {/* Hint */}
      <div className="text-center">
        <p className="text-gray-400 text-sm">
          Pas d'inquiétude, vous pourrez ajuster ces chiffres plus tard
        </p>
      </div>
    </div>
  );
}

export default FleetSizeStep;
